import { useSessionStorage, StorageSerializers } from '@vueuse/core'

const STORAGE_KEY = 'selfServiceCredentials'
const SESSION_TTL_MS = 30 * 60 * 1000 // 30 minutes

type TSelfServiceCredentials = {
  pnr: string
  surname: string
  timestamp: number
}

const isExpired = (timestamp: number | undefined | null): boolean => {
  if (!timestamp) return true

  return Date.now() - timestamp > SESSION_TTL_MS
}

export const useSessionStorageData = () => {
  const storedCredentials = useSessionStorage<TSelfServiceCredentials | null>(STORAGE_KEY, null, {
    serializer: StorageSerializers.object
  })

  const restoreCredentials = (): { pnr: string; surname: string } | null => {
    try {
      const credentials = storedCredentials.value
      if (!credentials?.pnr || !credentials?.surname) {
        return null
      }

      if (isExpired(credentials.timestamp)) {
        clearCredentials()
        return null
      }

      return {
        pnr: credentials.pnr,
        surname: credentials.surname
      }
    } catch (error) {
      console.error('Failed to restore self-service credentials from sessionStorage', error)
    }

    return null
  }

  const clearCredentials = () => {
    if (storedCredentials.value !== null) {
      storedCredentials.value = null
    }
  }

  const persistCredentials = (pnr: string, surname: string) => {
    if (!pnr || !surname) {
      clearCredentials()
      return
    }

    try {
      storedCredentials.value = {
        pnr,
        surname,
        timestamp: Date.now()
      }
    } catch (error) {
      console.error('Failed to persist self-service credentials to sessionStorage', error)
    }
  }

  return {
    restoreCredentials,
    clearCredentials,
    persistCredentials
  }
}
